import React, { useState } from "react";
import {
  Alert,
  Container,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import PaymentMethods from "./PaymentMethods";
import { useEffect } from "react";
import {
  getPlanInformation as getPlanInformationApi,
  cancelPlan as cancelPlanApi,
  switchPlan as switchPlanApi,
  unCancelSubscription as unCancelPlanApi,
} from "../../slices/thunks";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import moment from "moment";
import { Typography } from "@mui/material";
import { getLoggedinUser } from "../../utils/StorageManager";
import Loader from "../../Components/Common/Loader";
import { updateSettings } from "../../apiCalls/caller";
import { useNavigate } from "react-router-dom";

const Billing = () => {
  document.title = "Billing | Buyers Club";
  const dispatch = useDispatch();
  const userProfileSession = getLoggedinUser();
  const navigate = useNavigate();

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelFeedback, setCancelFeedback] = useState("");

  const selectBillingData = createSelector(
    (state) => state.Billing,
    (planInformation) => planInformation,
    (error) => error,
    (loading) => loading,
    (updating) => updating
  );
  // Inside your component
  const { planInformation, error, loading, updating } =
    useSelector(selectBillingData);

  useEffect(() => {
    if (userProfileSession.user_free === false) {
      dispatch(getPlanInformationApi());
    }
  }, []);
  const cancelPlan = () => {
    setShowCancelModal(true);
  };

  const handleCancelConfirm = () => {
    dispatch(
      cancelPlanApi({
        user_id: userProfileSession?.user_id ?? 0,
        reason: cancelReason || null,
        feedback: cancelFeedback || null,
      })
    );
    setShowCancelModal(false);
    setCancelReason("");
    setCancelFeedback("");
  };

  const handleCancelModalClose = () => {
    setShowCancelModal(false);
    setCancelReason("");
    setCancelFeedback("");
  };

  useEffect(() => {
    if (updating) {
      dispatch(getPlanInformationApi());
    }
  }, [updating]);

  // Helper to check if frequency is monthly (case-insensitive, handles 'month' or 'monthly')
  const isMonthly =
    planInformation?.frequency &&
    typeof planInformation.frequency === "string" &&
    (planInformation.frequency.toLowerCase() === "month" ||
      planInformation.frequency.toLowerCase() === "monthly");

  const switchPlan = () => {
    //switchPlan(frequency) 0 = monthly 1 = yearly
    const frequency = isMonthly ? 1 : 0;
    dispatch(switchPlanApi({ frequency: frequency }));
  };

  const handleUpgradeClick = () => {
    navigate("/subscription");
  };

  // Check if user has free trial
  const hasFreeTrialPlan = userProfileSession?.user_freeType === "trial";
  const trialEndDate = userProfileSession?.user_billingCycleEnd 
    ? new Date(userProfileSession.user_billingCycleEnd) 
    : null;
  const isTrialExpired = trialEndDate && trialEndDate < new Date();

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Billing" pageTitle="Billing" />
        
        {/* Free Trial Status */}
        {hasFreeTrialPlan && (
          <Alert color={isTrialExpired ? "danger" : "info"} className="mb-4">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-2">
                  <i className={`ri-${isTrialExpired ? 'error-warning' : 'information'}-line me-2`}></i>
                  {isTrialExpired ? "Your Free Trial Has Expired" : "Free Trial Active"}
                </h5>
                <p className="mb-0">
                  {isTrialExpired 
                    ? `Your trial expired on ${moment(trialEndDate).format("MMMM DD, YYYY")}. Upgrade to a paid plan to continue accessing all features.`
                    : `Your trial expires on ${moment(trialEndDate).format("MMMM DD, YYYY")}.`
                  }
                </p>
              </div>
              <Button color="primary" onClick={handleUpgradeClick}>
                <i className="ri-arrow-right-line me-1"></i>
                Upgrade to a Paid Plan
              </Button>
            </div>
          </Alert>
        )}

        {loading ? (
          <Loader />
        ) : planInformation ? (
          planInformation.status === "past_due" ? (
            <Alert color="danger" className="text-center">
              <strong> LAST PAYMENT WAS UNSUCCESSFUL</strong> We were unable to
              charge your last payment. Please update your payment information
              now to keep your subscription active. Without payment your account
              may be disabled within a few days.
              <br />
              <br />
              <button
                className="btn btn-danger"
                onClick={() => {
                  cancelPlan();
                }}
              >
                Cancel Plan
              </button>
            </Alert>
          ) : planInformation.status === "active" ? (
            <div className="alert alert-success" role="alert">
              <strong>
                <h2>Plan Information:</h2>
              </strong>
              <div>
                <Typography variant="h6" component="div">
                  Price:{" "}
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(planInformation.cost / 100)}
                </Typography>
                <Typography variant="h6" component="div">
                  Frequency: {isMonthly ? "Monthly" : "Yearly"}
                </Typography>
                <Typography variant="h6" component="div">
                  {planInformation.cancellationDate
                    ? "Termination scheduled at"
                    : "Next billing date"}
                  :{" "}
                  {moment(planInformation.nextBillingDate).format("MM-DD-YYYY")}
                </Typography>
              </div>
              {planInformation.cancellationDate ? (
                <div className="mt-3">
                  <Alert color="danger">
                    <Typography
                      variant="h6"
                      component="div"
                      className="text-danger"
                    >
                      Your subscription was canceled in:{" "}
                      {moment(planInformation.cancellationDate).format(
                        "MM-DD-YYYY"
                      )}
                    </Typography>
                  </Alert>
                  <button
                    className="btn btn-success"
                    onClick={() => {
                      dispatch(unCancelPlanApi());
                    }}
                  >
                    Resume Plan
                  </button>
                </div>
              ) : (
                <div className="mt-3">
                  <button
                    className="btn btn-primary me-2"
                    onClick={() => {
                      switchPlan();
                    }}
                  >
                    Change to {isMonthly ? "Yearly" : "Monthly"}
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => {
                      cancelPlan();
                    }}
                  >
                    Cancel Plan
                  </button>
                </div>
              )}
            </div>
          ) : planInformation.status === "deleted" ? (
            <Alert color="info" className="text-center">
              <strong>SUBSCRIPTION CANCELED</strong> Your subscription has been
              canceled and is no longer active.
              <br />
              <br />
              <button
                className="btn btn-primary"
                onClick={() => {
                  dispatch(getPlanInformationApi());
                }}
              >
                Refresh
              </button>
            </Alert>
          ) : (
            <Alert color="warning" className="text-center">
              <strong>SUBSCRIPTION INCOMPLETE</strong> Your subscription is not
              yet active. Please complete the payment process or contact support
              for assistance.
              <br />
              <br />
              <button
                className="btn btn-primary me-2"
                onClick={() => {
                  dispatch(getPlanInformationApi());
                }}
              >
                Refresh
              </button>
              <button
                className="btn btn-danger"
                onClick={() => {
                  cancelPlan();
                }}
              >
                Cancel Plan
              </button>
            </Alert>
          )
        ) : error ? (
          <Alert color="danger" className="text-center">
            <strong> We can not retrieve your subscription details.</strong>{" "}
            Please refresh your browser or click here to retry.
            <button
              className="btn btn-primary ms-2"
              onClick={() => {
                dispatch(getPlanInformationApi());
              }}
            >
              Retry
            </button>
          </Alert>
        ) : !hasFreeTrialPlan ? (
          <div className="alert alert-success" role="alert">
            <h1>You have a free plan.</h1>
          </div>
        ) : null}
        {!hasFreeTrialPlan && <PaymentMethods />}

        <Modal isOpen={showCancelModal} toggle={handleCancelModalClose} centered>
          <ModalHeader toggle={handleCancelModalClose}>
            Cancel Subscription
          </ModalHeader>
          <ModalBody>
            <p className="text-muted mb-3">
              We're sorry to see you go. Please let us know why you're
              canceling — your feedback helps us improve.
            </p>
            <FormGroup>
              <Label for="cancelReason">Reason for cancellation</Label>
              <Input
                type="select"
                id="cancelReason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              >
                <option value="">-- Select a reason --</option>
                <option value="too_expensive">Too expensive</option>
                <option value="missing_features">Missing features I need</option>
                <option value="not_using_enough">Not using it enough</option>
                <option value="switching_competitor">Switching to a competitor</option>
                <option value="technical_issues">Technical issues</option>
                <option value="other">Other</option>
              </Input>
            </FormGroup>
            <FormGroup>
              <Label for="cancelFeedback">Additional feedback (optional)</Label>
              <Input
                type="textarea"
                id="cancelFeedback"
                rows={4}
                placeholder="Tell us more about your experience..."
                value={cancelFeedback}
                onChange={(e) => setCancelFeedback(e.target.value)}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={handleCancelModalClose}>
              Keep my subscription
            </Button>
            <Button
              color="danger"
              onClick={handleCancelConfirm}
              disabled={!cancelReason}
            >
              Confirm Cancellation
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default Billing;
